import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, GameStateResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface SetGameStateBody {
  dataType: string;
  data?: string;
  dataBinary?: string;
  version?: number | null;
  checksum?: string;
  metadata?: Record<string, unknown>;
}

interface UseSetGameStateReturn {
  setGameState: (keyword: string, body: SetGameStateBody) => Promise<GameStateResponse>;
  saving: boolean;
  error: string | null;
}

export function useSetGameState(): UseSetGameStateReturn {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ keyword, body }: { keyword: string; body: SetGameStateBody }) =>
      api.setGameState(keyword, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gameState.all });
    },
  });

  return {
    setGameState: async (keyword: string, body: SetGameStateBody): Promise<GameStateResponse> => {
      const result = await mutation.mutateAsync({ keyword, body });
      return result.gameState;
    },
    saving: mutation.isPending,
    error:
      mutation.error instanceof Error
        ? mutation.error.message
        : mutation.error
          ? 'Failed to save game state'
          : null,
  };
}
